import { getCallerOid, getCallerSub } from '../../identity.js';
import { mockUser } from '../../mock/fixtures.js';

/**
 * Whether a message with the given `sender_user_id` was sent by the current caller (global-workspaces
 * phase 4). A shared thread (group/public) renders own messages as the familiar right-aligned bubble
 * and other members' messages as a left-aligned bubble with a speaker label.
 *
 * The conversation service stamps `sender_user_id = oid ?? internal user id`, so the caller matches on
 * either their JWT `oid` (SSO) or `sub` (dev login). An absent id — an optimistic just-sent row or a
 * legacy pre-phase-4 row — and the mock-fixture user id both count as own, which preserves the
 * single-author render for private/1:1 chats and the mock surface (no regression).
 */
export function isOwnSpeaker(senderUserId: string | null | undefined): boolean {
  if (senderUserId == null || senderUserId === mockUser.id) return true;
  return senderUserId === getCallerOid() || senderUserId === getCallerSub();
}

/**
 * The label shown above another member's bubble. Prefers the wire `sender_name` (the conversation
 * service stamps it from the speaker's identity on send), then the workspace member directory built by
 * {@link buildSpeakerDirectory}, then a neutral fallback.
 */
export function resolveSpeakerName(
  senderName: string | null | undefined,
  senderUserId: string | null | undefined,
  directory: ReadonlyMap<string, string>,
): string {
  const fromMessage = senderName?.trim();
  if (fromMessage) return fromMessage;
  const fromDirectory = senderUserId
    ? directory.get(senderUserId)?.trim()
    : undefined;
  if (fromDirectory) return fromDirectory;
  return 'Member';
}

/** One workspace member, as much of the directory entry as a speaker lookup needs. */
export interface SpeakerDirectoryMember {
  /** Internal user id (identity `users.id`) — the id the workspace member directory keys on. */
  userId: string;
  /** Entra object id, when the directory carries it; absent for a member with no linked SSO identity. */
  oid?: string | null;
  name: string | null;
}

/**
 * Build the lookup {@link resolveSpeakerName} falls back to, keyed by EVERY id space a member may
 * appear under. A message's `sender_user_id` is `oid ?? internal user id` (conversation-service's
 * `agentUserId`), while the workspace member directory keys on the internal id — so keying the map on
 * the internal id alone silently failed for every SSO speaker. Indexing each member under both ids
 * makes the lookup id-space agnostic: whichever id the message carries hits the same entry. Members
 * with no usable name are skipped so a blank can never win over the wire `sender_name`.
 */
export function buildSpeakerDirectory(
  members: ReadonlyArray<SpeakerDirectoryMember>,
): ReadonlyMap<string, string> {
  const directory = new Map<string, string>();
  for (const member of members) {
    const name = member.name?.trim();
    if (!name) continue;
    directory.set(member.userId, name);
    if (member.oid) directory.set(member.oid, name);
  }
  return directory;
}

/** First-letter initial used for a speaker's avatar; falls back to a bullet when unknown. */
export function speakerInitial(name: string): string {
  const first = name.trim().charAt(0);
  return first ? first.toUpperCase() : '•';
}
